/*
 * getLink.tsx
 *
 * Created by Dr. Maximillian Dornseif 2024-02-26 in @hudora/hd-link 1.0.0
 * Copyright (c) 2024 HUDORA GmbH
 */

interface ILinkDef {
  pattern: string
  link: string
  app?: string
  fixer?: (arg0: string) => string
}

const patterns: ILinkDef[] = [
  { pattern: 'PO\\d{6,16}', link: '/einkauf/b/{}/', app: 'huFob3' },
  { pattern: 'SP\\d{5}', link: '/einkauf/l/{}/', app: 'huFob3' },
  { pattern: 'EK\\d{6}', link: '/einkauf/ek/{}/', app: 'huFob3' },
  { pattern: 'FD\\d{6}', link: '/fob/o/{}', app: 'huFob3' },
  { pattern: 'IND\\d{6}', link: ' /belege/{}', app: 'huFob3' },
  { pattern: 'INA\\d{6}', link: ' /belege/{}', app: 'huFob3' },

  // der Regular Expression für Artikelnummern ist beschränkter. Wir wollen ja nicht
  // überall Nummern matchen.
  { pattern: 'P[A-Z]\\d{3}', link: '/wawi/prod/{}/', app: 'huWaWi3' },
  { pattern: '\\d{5}', link: '/wawi/item/{}/', app: 'huWaWi3' },
  { pattern: '\\d{5}x', link: '/ic/mastersku/{}/', app: 'huWaWi3' },
  { pattern: '\\d{5}\\x\\d{2}', link: '/ic/sku/{}/', app: 'huWaWi3' },

  // Kunden, Gruppen / Verbände
  { pattern: 'SV\\d{5}', link: '/stamm/v/{}/', app: 'huWaWi3' },
  { pattern: 'KG\\d{5}', link: '/wawi/g/{}/', app: 'huWaWi3' },
  { pattern: 'SC\\d{5}', link: '/wawi/k/{}/', app: 'huWaWi3' },
  {
    pattern: 'VASC\\d{5}',
    link: '/stamm/k/{}/versand',
    app: 'huWaWi3',
    fixer: (designator) => designator.replace('VASC', 'SC'),
  },
  { pattern: 'VA\\d{5,16}', link: '/wawi/va/{}/', app: 'huWaWi3' },
  { pattern: 'VA[A-Z]{0,3}\\d{5,16}', link: '/wawi/va/{}/', app: 'huWaWi3' },

  // Aufträge etc
  { pattern: 'HA\\d{9,16}', link: '/wawi/ic/a/{}/', app: 'huWaWi3' },
  { pattern: 'HE\\d{13,22}', link: '/wawi/ic/e/{}/', app: 'huWaWi3' },
  { pattern: 'KR\\d{9,16}', link: '/wawi/ic/rk/{}/', app: 'huWaWi3' },

  { pattern: 'PL\\d{9,16}', link: '/wawi/preise/{}/', app: 'huWaWi3' },
  { pattern: 'QX\\d{3,3}', link: '/wawi/qx/{}/', app: 'huWaWi3' },

  // Lagerkram
  { pattern: 'LG\\d{1,3}', link: '/ic/lager/{}/', app: 'huWaWi3' },
  { pattern: 'LB\\d{6,20}', link: '/ic/bestandsbuch/{}/', app: 'huWaWi3' },
  { pattern: 'LK\\d{11,16}Zd+', link: '/ic/zuteilung/{}/', app: 'huWaWi3' },
  { pattern: 'LK\\d{11,16}', link: '/ic/kommi/{}/', app: 'huWaWi3' },
  {
    pattern: 'IB\\d{6,20}',
    link: '/ic/inventur/bestaetigungen/{}',
    app: 'huWaWi3',
  },

  { pattern: 'LV\\d{6,16}', link: '/belege/{}/', app: 'huWaWi3' },
  { pattern: 'LSLK\\d{6,16}', link: '/belege/{}/', app: 'huWaWi3' },
  { pattern: 'LRLK\\d{6,16}', link: '/belege/{}/', app: 'huWaWi3' },
  { pattern: 'LS\\d{11,16}', link: '/belege/{}/', app: 'huWaWi3' },

  { pattern: 'RE\\d{6,7}', link: '/belege/{}/', app: 'huWaWi3' },
  { pattern: 'RE\\d{6,7}S', link: '/belege/{}/', app: 'huWaWi3' },
  { pattern: 'RK\\d{6,7}', link: '/belege/{}/', app: 'huWaWi3' },
  { pattern: 'AB\\d{6,7}', link: '/belege/{}/', app: 'huWaWi3' },
  { pattern: 'BB\\d{6,7}', link: '/belege/{}/', app: 'huWaWi3' },
  { pattern: 'PR\\d{6,7}', link: '/belege/{}/', app: 'huWaWi3' },

  { pattern: 'AS\\d{6,7}', link: '/belege/{}/', app: 'huWaWi3' },

  {
    pattern: '0142\\d{10}[A-Z0-9]',
    link: 'https://tracking.dpd.de/status/de_DE/parcel/{}',
  },

  {
    pattern: 'AP\\d{5,6}',
    link: '/stamm/aaa/permissionGui/{}',
    app: 'huWaWi3',
  },
  {
    pattern: 'AP\\[a-z0-9]{7}',
    link: '/stamm/aaa/permissionGui/{}',
    app: 'huWaWi3',
  },
  { pattern: 'AR\\d{6,16}', link: '/stamm/aaa/role/{}', app: 'huWaWi3' },
  { pattern: 'ROLE0', link: '/stamm/aaa/role/{}', app: 'huWaWi3' },
]
const app2urlprefix: Record<string, string> = {
  huWaWi3: 'https://huwawi3.hudora.de',
  eAP3: 'https://eap3.hudora.de',
  huFob3: 'https://hufob3.hudora.hk',
}

export function getLink(designator: string, appName: string = 'huWaWi3') {
  const regexpen = patterns.map((x) => {
    let link = x.link
    let currentapp = appName
    if (/hufob/i.test(window.location.href)) {
      currentapp = 'huFob3'
    }
    if (/eap3/i.test(window.location.href)) {
      currentapp = 'eAP3'
    }

    if (x.app && currentapp !== x.app) {
      link = `${app2urlprefix[x.app as keyof typeof app2urlprefix]}${link}`
    }

    const fixer = x.fixer || ((x) => x)

    return { re: new RegExp(`^${x.pattern}$`), link, fixer }
  })

  for (const pat of regexpen) {
    if (pat.re.test(designator)) {
      return pat.link.replace('{}', encodeURIComponent(pat.fixer(designator)))
    }
  }
  return undefined
}
